// frontend/src/components/HoursManagement/MobileHoursView.js

import React, { useState, useMemo, useEffect } from 'react';

const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const MobileHoursView = ({
  weekDates,
  activeProjects,
  inactiveProjects,
  showOtherProjects,
  setShowOtherProjects,
  getHours,
  handleHourChange,
  handleBlur,
  employee,
  plannedHoursByProject,
  slotHoursByProject,
  getDayTotal,
}) => {
  const [dayIndex, setDayIndex] = useState(0);

  // Select current day by default when it exists in the current week
  useEffect(() => {
    if (!weekDates || weekDates.length === 0) return;
    const today = new Date();
    today.setHours(0,0,0,0);
    const idx = weekDates.findIndex(d => {
      const dd = new Date(d);
      dd.setHours(0,0,0,0);
      return dd.getTime() === today.getTime();
    });
    if (idx >= 0) setDayIndex(idx);
  }, [weekDates]);

  const selectedDate = weekDates[dayIndex];
  const selectedDateLabel = useMemo(() => {
    const d = selectedDate;
    const name = dayNames[dayIndex];
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${name} ${day}/${month}`;
  }, [selectedDate, dayIndex]);

  const ProjectEntry = ({ project }) => {
    const baseValue = employee ? getHours(employee.id, project.id, selectedDate) : 0;
    const [focused, setFocused] = useState(false);
    const [localValue, setLocalValue] = useState('');
    const displayValue = focused ? localValue : (baseValue ? String(baseValue) : '');

    const commit = (raw) => {
      const normalized = String(raw).replace(',', '.');
      handleHourChange(project.id, selectedDate, normalized);
      handleBlur();
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="font-medium text-gray-800 truncate">{project.name}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            Slot: {slotHoursByProject[project.id] || 0}h · Plan: {plannedHoursByProject[project.id] || 0}h
          </div>
        </div>
        <input
          type="text"
          inputMode="decimal"
          enterKeyHint="done"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          pattern="[0-9]*[.,]?[0-9]*"
          value={displayValue}
          onFocus={() => { setFocused(true); setLocalValue(baseValue ? String(baseValue) : ''); }}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={() => { setFocused(false); commit(localValue); }}
          data-hour-input="1"
          data-project-id={project.id}
          data-date={selectedDate.toLocaleDateString('sv-SE')}
          className="ml-3 w-20 text-right border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Onglets jours */}
      <div className="sticky top-0 z-10 -mx-6 px-6 pt-1 pb-2 bg-white">
        <div className="grid grid-cols-7 gap-1">
          {weekDates.map((d, idx) => (
            <button
              key={idx}
              onClick={() => setDayIndex(idx)}
              className={`px-2 py-2 rounded-full border text-xs leading-4 ${
                dayIndex === idx ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-700 border-gray-200'
              }`}
            >
              <div>{dayNames[idx]}</div>
              <div className={`text-[10px] ${dayIndex === idx ? 'text-blue-100' : 'text-gray-500'}`}>
                {d.getDate().toString().padStart(2, '0')}/{(d.getMonth() + 1).toString().padStart(2, '0')}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* En-tête date + total jour */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">{selectedDateLabel}</div>
        <div className="text-sm font-medium">Total: {getDayTotal(selectedDate)}h</div>
      </div>

      {/* Projets actifs */}
      <div className="flex flex-col gap-2">
        {activeProjects.length === 0 ? (
          <div className="text-center text-gray-500 italic py-4">Aucun projet actif</div>
        ) : (
          activeProjects.map(project => (
            <ProjectEntry key={project.id} project={project} />
          ))
        )}
      </div>

      {/* Autres projets (repliable) */}
      {inactiveProjects.length > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setShowOtherProjects(!showOtherProjects)}
            className="w-full text-left text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2"
          >
            {showOtherProjects ? 'Masquer' : 'Afficher'} autres projets ({inactiveProjects.length})
          </button>
          {showOtherProjects && (
            <div className="mt-2 flex flex-col gap-2">
              {inactiveProjects.map(project => (
                <ProjectEntry key={project.id} project={project} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileHoursView;
